<script setup lang="ts">
type StudentUserData = App.Auth.Data.StudentUserData;
type StudentParentData = App.Students.Data.StudentParentData;

import { BadgeColor } from '@/hooks/useBadgeClasses';
import { ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import { formatDate } from '@/utils/date';

import Button from '@/components/button/Button.vue';
import Card from '@/components/studentParentApp/card/Card.vue';
import CardDefinitionList from '@/components/studentParentApp/card/CardDefinitionList.vue';
import CardDefinitionListItem from '@/components/studentParentApp/card/CardDefinitionListItem.vue';
import CardHeader from '@/components/studentParentApp/card/CardHeader.vue';
import CardList from '@/components/studentParentApp/card/CardList.vue';
import CardListItem from '@/components/studentParentApp/card/CardListItem.vue';
import CardListUserItem from '@/components/studentParentApp/card/CardListUserItem.vue';
import Logout from '@/components/studentParentApp/logout/Logout.vue';
import PageContainer from '@/components/studentParentApp/container/PageContainer.vue';
import PageSection from '@/components/studentParentApp/PageSection/PageSection.vue';

import Calendar from '@/icons/line/calendar.svg';
import Check from '@/icons/line/check.svg';
import Envelope from '@/icons/line/envelope.svg';
import ExclamationCircle from '@/icons/line/exclamation-circle.svg';
import Lock from '@/icons/line/lock.svg';
import Phone from '@/icons/line/phone.svg';
import User from '@/icons/line/user.svg';
import Hash from '@/icons/line/hash.svg';
import GraduationCap from '@/icons/line/graduation-cap.svg';
import Star from '@/icons/line/star.svg';

const props = defineProps<{
  user: StudentUserData;
  parents: StudentParentData[];
}>();

const userPhoneNumber = computed(() => {
  if (props.user.cell_phone) {
    return props.user.cell_phone;
  }

  if (props.user.home_phone) {
    return props.user.home_phone;
  }

  return '--';
});

const hasMultiplePhoneNumbers = computed(() => {
  return !!(props.user.cell_phone && props.user.home_phone);
});
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <PageContainer>
    <div class="flex flex-col gap-y-3 pb-5">
      <Card :href="route('student.profile.edit')">
        <template #header>
          <CardHeader :title="user.name" :user="user" />
        </template>
        <CardDefinitionList>
          <CardDefinitionListItem
            :icon="Calendar"
            label="Date of birth"
            :value="(user.dob ? formatDate(user.dob) : undefined) ?? undefined"
          />
          <CardDefinitionListItem :icon="User" label="Gender" :value="user.gender?.label" />
          <CardDefinitionListItem :icon="User" label="Ethnicity" :value="user.ethnicity?.label" />
        </CardDefinitionList>
      </Card>
      <CardList>
        <CardListItem
          :href="route('student.profile.email.edit')"
          :icon="Envelope"
          label="Email"
          :value="
            user.email_verifications?.length
              ? user.email_verifications[0].new_email
              : user.email ?? '--'
          "
          :badgeLabel="user.email_verifications?.length ? 'Unverified' : undefined"
          :badgeColor="BadgeColor.warning"
          :badgeIconLeft="ExclamationCircle"
        />
        <CardListItem
          :href="route('student.profile.phone-numbers.edit')"
          :icon="Phone"
          label="Phone"
          :value="userPhoneNumber"
          :badgeLabel="hasMultiplePhoneNumbers ? '1 more' : undefined"
        />
        <CardListItem
          :href="route('student.profile.security.show')"
          :icon="Lock"
          label="Password & 2FA"
          value="••••••••"
          :badgeLabel="user.two_factor_status?.value === 'verified' ? '2FA' : undefined"
          :badgeIconLeft="Check"
        />
      </CardList>
    </div>
  </PageContainer>
  <PageSection title="Enrolment and Academics">
    <Card :href="route('student.profile.enrolment.edit')">
      <template #header>
        <CardHeader :title="user.grade?.label">
          <template #subtitle>
            <div class="flex gap-x-1.5">
              <span class="truncate whitespace-nowrap">{{ user.school?.name }}</span>
              <template v-if="user.class_of">
                <span class="text-slate-400">•</span>
                <span class="whitespace-nowrap">Class of {{ user.class_of }}</span>
              </template>
            </div>
          </template>
        </CardHeader>
      </template>
      <CardDefinitionList>
        <CardDefinitionListItem :icon="Hash" label="GPA" :value="user.gpa ?? undefined" />
        <CardDefinitionListItem
          :icon="GraduationCap"
          label="Post-secondary plans"
          :value="user?.post_secondary_plans?.[0]?.post_secondary_plan?.label"
          :badgeLabel="
            user?.post_secondary_plans && user.post_secondary_plans.length > 1
              ? `${user.post_secondary_plans.length - 1} more`
              : undefined
          "
        />
        <CardDefinitionListItem
          :icon="Star"
          label="Post-secondary major"
          :value="user?.majors?.[0]?.major?.label"
          :badgeLabel="
            user?.majors && user.majors.length > 1 ? `${user.majors.length - 1} more` : undefined
          "
        />
      </CardDefinitionList>
    </Card>
  </PageSection>
  <PageSection title="Parent/Guardian">
    <div class="flex flex-col gap-y-3">
      <CardList v-if="parents.length">
        <CardListUserItem
          v-for="parent in parents"
          :key="parent.id"
          :href="route('student.profile.parent.edit', { parent: parent.id })"
          :user="parent"
        />
      </CardList>
      <Button
        :href="route('student.profile.parent.create')"
        :size="ButtonSize.lg"
        :variant="ButtonVariant.outlined"
      >
        Add parent or guardian
      </Button>
    </div>
  </PageSection>
  <Logout />
</template>
